import { useCallback, useState } from 'react';
import { FabricImage } from 'fabric';
import { useDesignStore } from '@/stores';
import { logger } from '@/utils/logger';
import { MAX_FILE_SIZE } from '@/config/constants';
import { uploadAsset } from '@/api/assets';
import { setAssetProperties } from '@/utils/canvasSerializer';
import {
  cropImageToVisibleBounds,
  downscaleBlobUrl,
} from '@/utils/detectVisibleBounds';
import { useCanvasStateManager } from '@/stores/canvasStateManager';
import { computeFittedPlacement } from '@/services/canvas/fittedPlacement';

/**
 * Callbacks for the background S3 upload that follows the canvas placement.
 * The upload is fire-and-forget for the canvas (the image is already
 * placed); these let a caller surface the outcome — the Direct Sale upload
 * panel stays open until one of them fires.
 */
export interface UploadOutcomeCallbacks {
  onUploadComplete?: () => void;
  onUploadFailed?: () => void;
}

/** Where a new image lands. The Seller flow keeps the mockup centre. */
export type ImagePlacement = 'canvas-center' | 'print-area-fitted';

/**
 * Natural size of the uploaded original: from the upload response, or read
 * from the file itself when the response has none. Reading it decodes the
 * image once more but keeps no canvas, which is what a phone cannot spare.
 */
async function originalSizeOf(asset: {
  url: string;
  width: number | null;
  height: number | null;
}): Promise<{ originalWidth: number; originalHeight: number } | null> {
  if (asset.width && asset.height) {
    return { originalWidth: asset.width, originalHeight: asset.height };
  }
  return new Promise((resolve) => {
    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = () =>
      resolve(
        probe.naturalWidth && probe.naturalHeight
          ? {
              originalWidth: probe.naturalWidth,
              originalHeight: probe.naturalHeight,
            }
          : null
      );
    probe.onerror = () => resolve(null);
    probe.src = asset.url;
  });
}

export interface AddImageOptions {
  placement?: ImagePlacement;
  /**
   * Longest edge of the bitmap kept on the canvas. The file itself goes up
   * unchanged; only what the browser holds and draws is reduced, and the
   * uploaded original is not loaded back into it. For phones, where the
   * canvas memory a page may use is small.
   */
  previewMaxEdge?: number;
}

/**
 * Scale and position for a new image. 'print-area-fitted' fits it into the
 * active print area, high on it (see computeFittedPlacement). By default,
 * and without a known print area, it is the mockup centre at no more than
 * 80 % of the canvas, never scaled up.
 */
function placeNewImage(
  canvas: { width?: number; height?: number },
  imageWidth: number,
  imageHeight: number,
  placement: ImagePlacement = 'canvas-center'
): { left: number; top: number; scale: number } {
  const context = useCanvasStateManager.getState().activePrintContext;
  if (placement === 'print-area-fitted' && context) {
    const fitted = computeFittedPlacement(imageWidth, imageHeight, {
      x: context.offsetX,
      y: context.offsetY,
      width: context.widthPx,
      height: context.heightPx,
    });
    if (fitted) {
      return { left: fitted.centerX, top: fitted.centerY, scale: fitted.scale };
    }
  }
  const width = canvas.width ?? 0;
  const height = canvas.height ?? 0;
  return {
    left: width / 2,
    top: height / 2,
    scale: Math.min(
      (width * 0.8) / imageWidth,
      (height * 0.8) / imageHeight,
      1
    ),
  };
}

/**
 * Hook for image operations on the canvas.
 *
 * addImageFromFile: adds an image to the canvas, crops transparent areas,
 * uploads to S3, and replaces the local blob with the permanent URL.
 */
export function useImageOperations() {
  const { canvas, saveToHistory } = useDesignStore();
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Add image from a File object.
   * 1. Crop transparent areas
   * 2. Add to canvas with a temp assetId
   * 3. Upload to S3 in the background
   * 4. On success, update assetId/assetType and replace image element with S3 URL
   */
  const addImageFromFile = useCallback(
    async (
      file: File,
      callbacks?: UploadOutcomeCallbacks,
      options?: AddImageOptions
    ): Promise<FabricImage | null> => {
      if (!canvas) return null;

      setIsLoading(true);

      let originalBlobUrl: string | undefined;
      let croppedBlobUrl: string | undefined;

      try {
        originalBlobUrl = URL.createObjectURL(file);

        const cropResult = await cropImageToVisibleBounds(originalBlobUrl);
        croppedBlobUrl = cropResult.blobUrl;
        if (options?.previewMaxEdge) {
          const reduced = await downscaleBlobUrl(
            croppedBlobUrl,
            options.previewMaxEdge
          );
          if (reduced !== croppedBlobUrl) {
            if (croppedBlobUrl !== originalBlobUrl) {
              URL.revokeObjectURL(croppedBlobUrl);
            }
            croppedBlobUrl = reduced;
          }
        }

        const img = await FabricImage.fromURL(croppedBlobUrl, {
          crossOrigin: 'anonymous',
        });

        if (img._element) {
          (img._element as HTMLImageElement).crossOrigin = 'anonymous';
        }
        (img as unknown as { crossOrigin?: string }).crossOrigin = 'anonymous';

        const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        const { left, top, scale } = placeNewImage(
          canvas,
          img.width || 1,
          img.height || 1,
          options?.placement
        );

        img.set({
          left,
          top,
          originX: 'center',
          originY: 'center',
          scaleX: scale,
          scaleY: scale,
          name: file.name,
        });

        setAssetProperties(img, {
          assetId: tempId,
          assetType: 'local',
          _uploadStatus: 'pending',
        });

        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.requestRenderAll();
        saveToHistory('addImage');
        setIsLoading(false);

        uploadAsset(file)
          .then(async (asset) => {
            logger.debug('ImageOperations', `Asset uploaded: ${asset.id}`);
            setAssetProperties(img, {
              assetId: asset.id,
              assetType: 's3',
              _uploadStatus: 'completed',
            });

            try {
              if (options?.previewMaxEdge) {
                // Keep the reduced bitmap on the canvas, but let the saved
                // object describe the original: later loads draw the
                // original, and with the reduced width/height they would
                // show only its top-left corner (describeOriginalFootprint).
                const original = await originalSizeOf(asset);
                (img as unknown as { src?: string }).src = asset.url;
                setAssetProperties(img, {
                  originalUrl: asset.url,
                  ...(original ?? {}),
                });
                // The full-size file is not needed any more; the reduced
                // bitmap stays the element and keeps its own URL.
                if (originalBlobUrl && originalBlobUrl !== croppedBlobUrl) {
                  URL.revokeObjectURL(originalBlobUrl);
                }
                if (canvas.getObjects().includes(img)) {
                  canvas.fire('object:modified', { target: img });
                }
                callbacks?.onUploadComplete?.();
                return;
              }
              const newImg = await FabricImage.fromURL(asset.url, {
                crossOrigin: 'anonymous',
              });

              const newImgElement = newImg._element as
                | HTMLImageElement
                | undefined;

              if (newImgElement) {
                // Swapping the element alone keeps the object's old width
                // and height, and Fabric then draws only a top-left slice
                // of the new bitmap. Invisible while both are the same
                // size, which is the case for PNG and JPEG -- but the
                // server rasterises an SVG to a fixed 2400 px, so the
                // footprint has to be carried over explicitly.
                const previousWidth = img.width ?? newImgElement.naturalWidth;
                const previousHeight =
                  img.height ?? newImgElement.naturalHeight;
                const previousScaleX = img.scaleX ?? 1;
                const previousScaleY = img.scaleY ?? 1;

                img._element = newImgElement;
                (img as unknown as { src?: string }).src = asset.url;
                newImgElement.crossOrigin = 'anonymous';
                (img as unknown as { crossOrigin?: string }).crossOrigin =
                  'anonymous';

                const nextWidth = newImgElement.naturalWidth || previousWidth;
                const nextHeight =
                  newImgElement.naturalHeight || previousHeight;
                img.width = nextWidth;
                img.height = nextHeight;
                img.scaleX = (previousScaleX * previousWidth) / nextWidth;
                img.scaleY = (previousScaleY * previousHeight) / nextHeight;

                img.setCoords();
                canvas.requestRenderAll();
              } else {
                const imgElement = img._element as HTMLImageElement | undefined;
                if (imgElement) {
                  imgElement.crossOrigin = 'anonymous';
                }
                (img as unknown as { crossOrigin?: string }).crossOrigin =
                  'anonymous';
                img.setSrc(asset.url);
                if (img._element) {
                  (img._element as HTMLImageElement).crossOrigin = 'anonymous';
                }
                canvas.requestRenderAll();
              }

              if (originalBlobUrl) {
                URL.revokeObjectURL(originalBlobUrl);
              }
              if (croppedBlobUrl && croppedBlobUrl !== originalBlobUrl) {
                URL.revokeObjectURL(croppedBlobUrl);
              }
            } catch (updateError) {
              logger.warn(
                'ImageOperations',
                'Failed to update image source after upload:',
                updateError
              );
            }
            // Serialization filters temp_ assets, so the save that ran at
            // placement stored the position WITHOUT this image. Announce the
            // now-permanent assetId so save listeners re-persist — without
            // this, nothing re-saves until the user happens to modify the
            // object (same fix as CanvasEditor's own upload path).
            if (canvas.getObjects().includes(img)) {
              canvas.fire('object:modified', { target: img });
            }
            callbacks?.onUploadComplete?.();
          })
          .catch((error) => {
            logger.error('ImageOperations', 'Asset upload failed:', error);
            setAssetProperties(img, { _uploadStatus: 'failed' });
            callbacks?.onUploadFailed?.();
          });

        return img;
      } catch (error) {
        logger.error('ImageOperations', 'Error adding image:', error);
        if (originalBlobUrl) {
          try {
            URL.revokeObjectURL(originalBlobUrl);
          } catch {
            // ignore cleanup errors
          }
        }
        if (croppedBlobUrl && croppedBlobUrl !== originalBlobUrl) {
          try {
            URL.revokeObjectURL(croppedBlobUrl);
          } catch {
            // ignore cleanup errors
          }
        }
        setIsLoading(false);
        return null;
      }
    },
    [canvas, saveToHistory]
  );

  /**
   * Add image from URL.
   * Stores original URL for CDN URL regeneration.
   */
  const addImageFromUrl = useCallback(
    async (
      url: string,
      assetId?: string,
      options?: AddImageOptions
    ): Promise<FabricImage | null> => {
      if (!canvas) return null;

      setIsLoading(true);

      try {
        const img = await FabricImage.fromURL(url, {
          crossOrigin: 'anonymous',
        });

        const { left, top, scale } = placeNewImage(
          canvas,
          img.width || 1,
          img.height || 1,
          options?.placement
        );

        const finalAssetId =
          assetId ||
          `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        img.set({
          left,
          top,
          originX: 'center',
          originY: 'center',
          scaleX: scale,
          scaleY: scale,
        });

        setAssetProperties(img, {
          assetId: finalAssetId,
          assetType: assetId ? 's3' : 'local',
          originalUrl: url,
          _uploadStatus: assetId ? 'completed' : 'pending',
        });

        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.requestRenderAll();
        saveToHistory('addImage');

        return img;
      } catch (error) {
        logger.error('ImageOperations', 'Error adding image from URL:', error);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [canvas, saveToHistory]
  );

  /**
   * Handle file input change event
   */
  const handleFileInput = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        logger.warn(
          'ImageOperations',
          'Invalid file type. Please select an image.'
        );
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        logger.warn('ImageOperations', 'File too large. Maximum size is 15MB.');
        return;
      }

      await addImageFromFile(file);

      event.target.value = '';
    },
    [addImageFromFile]
  );

  return {
    addImageFromFile,
    addImageFromUrl,
    handleFileInput,
    isLoading,
  };
}
